/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/js/Axeptio.js
class Axeptio {
  constructor() {
    this.footerLinksSelector = '.footer .footer__links a';
  }

  init = () => {
    const footerLinks = document.querySelectorAll(this.footerLinksSelector);

    if (footerLinks.length === 0) {
      return;
    }

    footerLinks.forEach((link) => {
      if (link.href.includes('#replaceAxeptio')) {
        // eslint-disable-next-line no-script-url
        link.href = 'javascript:openAxeptioCookies()';
      }
    });
  };
}

;// ./src/js/DeliveryDelay.js
class DeliveryDelay {
  constructor() {
    this.textIconSelector = '.wq-delivery-infos__text-icon';
    this.firstDateSelector = '.wq-delivery-infos__text-bold-date--first';
    this.secondDateSelector = '.wq-delivery-infos__text-bold-date--second';
    this.holidays = [];
  }

  init = async () => {
    const textIcons = document.querySelectorAll(this.textIconSelector);

    if (!textIcons.length > 0) {
      return;
    }

    const today = new Date();
    const year = today.getFullYear();

    try {
      const response = await fetch(
        `https://calendrier.api.gouv.fr/jours-feries/metropole/${year}.json`
      );
      const data = await response.json();
      this.holidays = Object.keys(data).map((date) => new Date(date));
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Erreur lors du chargement des jours fériés :', error);
    }

    textIcons.forEach((textIcon) => {
      const firstDate = textIcon.querySelector(this.firstDateSelector);
      const secondDate = textIcon.querySelector(this.secondDateSelector);
      const delayMin = firstDate.dataset.deliveryDelayMin;
      const delayMax = secondDate.dataset.deliveryDelayMax;

      if (!firstDate || !secondDate) {
        return;
      }

      const options = {
        month: 'short',
        day: 'numeric',
      };

      const estimatedDate = this.addWorkingDays(today, delayMin);
      firstDate.innerText = estimatedDate.toLocaleDateString('fr-FR', options);

      const estimatedDateMax = this.addWorkingDays(today, delayMax);
      secondDate.innerText = estimatedDateMax.toLocaleDateString(
        'fr-FR',
        options
      );

      textIcon.style.opacity = 1;
    });
  };

  isWorkingDay = (date) => {
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6; // Sunday = 0, Saturday = 6
    const isHoliday = this.holidays.some(
      (holiday) => holiday.toDateString() === date.toDateString()
    );
    return !isWeekend && !isHoliday;
  };

  addWorkingDays = (startDate, daysToAdd) => {
    const date = new Date(startDate);
    let addedDays = 0;
    while (addedDays < daysToAdd) {
      date.setDate(date.getDate() + 1);
      if (this.isWorkingDay(date)) {
        addedDays++;
      }
    }
    return date;
  };
}

;// ./src/js/HeaderScrollHide.js
class HeaderScrollHide {
  constructor() {
    this.headerSelector = '.js-header';
    this.hiddenClass = 'header--hidden-on-scroll';
    this.scrollThreshold = 5;
    this.abortController = null;
  }

  init = () => {
    const header = document.querySelector(this.headerSelector);

    if (!header || !header.classList.contains('is-sticky')) return;

    this.destroy();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    let activationScrollY = 0;
    let lastScrollY = window.scrollY;

    const refreshMeasurements = () => {
      header.classList.remove(this.hiddenClass);
      activationScrollY = header.getBoundingClientRect().top + window.scrollY;
    };

    const setHidden = (hidden) => {
      header.classList.toggle(this.hiddenClass, hidden);
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const scrollDelta = scrollY - lastScrollY;

      if (Math.abs(scrollDelta) < this.scrollThreshold) return;

      lastScrollY = scrollY;

      if (scrollY <= activationScrollY) {
        setHidden(false);
        return;
      }

      setHidden(scrollDelta > 0);
    };

    const handleResize = () => {
      refreshMeasurements();
      handleScroll();
    };

    refreshMeasurements();
    handleScroll();

    window.addEventListener('scroll', handleScroll, { signal });
    window.addEventListener('resize', handleResize, { signal });

    document.addEventListener('shopify:section:load', this.handleSectionLoad, {
      signal,
    });
  };

  handleSectionLoad = (event) => {
    if (event.target.querySelector?.(this.headerSelector)) {
      this.init();
    }
  };

  destroy = () => {
    this.abortController?.abort();
    this.abortController = null;
  };
}

;// ./src/js/MenuUtilsNav.js
class MenuUtilsNav {
  constructor() {
    this.utilsNavSelector = '.js-utils-nav';
    this.headerSelector = '.js-header';
    this.dropdownItemSelector = '.header__utils-nav-item--dropdown';
    this.dropdownItemOpenClass = 'header__utils-nav-item--open';
    this.dropdownOpenClass = 'is-open';
    this.headerOpenClass = 'is-megamenu-open';
    this.abortController = null;
  }

  init = () => {
    const utilsNav = document.querySelector(this.utilsNavSelector);

    if (!utilsNav) {
      return;
    }

    this.destroy();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const header =
      utilsNav.closest(this.headerSelector) ||
      document.querySelector(this.headerSelector);

    if (!header) {
      return;
    }

    const dropdownItems = utilsNav.querySelectorAll(this.dropdownItemSelector);

    const closeMainNavDropdowns = () => {
      document
        .querySelectorAll('nav.js-nav .js-dropdown.is-visible')
        .forEach((dropdown) => {
          dropdown.classList.remove('is-visible');
        });

      document
        .querySelectorAll('nav.js-nav .header__nav-sublinks.is-visible')
        .forEach((sublink) => {
          sublink.classList.remove('is-visible');
        });
    };

    const closeAll = () => {
      dropdownItems.forEach((item) => {
        item.classList.remove(this.dropdownItemOpenClass);

        const link = item.querySelector('.header__utils-nav-link');
        const dropdown = item.querySelector('.header__utils-nav-dropdown');

        if (link) {
          link.setAttribute('aria-expanded', 'false');
        }

        if (dropdown) {
          dropdown.classList.remove(this.dropdownOpenClass);
        }
      });

      if (!document.querySelector('nav.js-nav .js-dropdown.is-visible')) {
        header.classList.remove(this.headerOpenClass);
      }
    };

    const openItem = (item) => {
      closeMainNavDropdowns();
      closeAll();

      const dropdown = item.querySelector('.header__utils-nav-dropdown');
      const link = item.querySelector('.header__utils-nav-link');

      if (!dropdown) {
        return;
      }

      item.classList.add(this.dropdownItemOpenClass);
      dropdown.classList.add(this.dropdownOpenClass);

      if (link) {
        link.setAttribute('aria-expanded', 'true');
      }

      header.classList.add(this.headerOpenClass);
    };

    dropdownItems.forEach((item) => {
      item.addEventListener(
        'mouseenter',
        () => {
          openItem(item);
        },
        { signal }
      );

      item.addEventListener(
        'mouseleave',
        (event) => {
          const related = event.relatedTarget;

          if (related && item.contains(related)) {
            return;
          }

          closeAll();
        },
        { signal }
      );
    });

    utilsNav
      .querySelectorAll(
        `.header__utils-nav-item:not(${this.dropdownItemSelector})`
      )
      .forEach((item) => {
        item.addEventListener('mouseenter', closeAll, { signal });
      });

    utilsNav.addEventListener('mouseleave', closeAll, { signal });

    document.addEventListener('shopify:section:load', this.handleSectionLoad, {
      signal,
    });
  };

  handleSectionLoad = (event) => {
    if (event.target.querySelector?.(this.utilsNavSelector)) {
      this.init();
    }
  };

  destroy = () => {
    this.abortController?.abort();
    this.abortController = null;
  };
}

;// ./src/js/ShopTheLook.js
class ShopTheLook {
  constructor() {
    this.sliderComponentSelector = 'shop-the-look-slider';
    this.dualSwiperSelector = '.shop-the-look__swiper--2';
    this.abortController = null;
    this.dualSliderParams = {
      slidesPerView: 2,
      slidesPerGroup: 1,
      spaceBetween: 16,
      breakpoints: {
        1200: { slidesPerView: 2, slidesPerGroup: 1, spaceBetween: 32 },
      },
    };
  }

  init = () => {
    const dualSwipers = document.querySelectorAll(
      `${this.sliderComponentSelector} ${this.dualSwiperSelector}`
    );

    if (!dualSwipers.length) {
      return;
    }

    this.destroy();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    dualSwipers.forEach((swiperEl) => {
      const sliderEl = swiperEl.closest(this.sliderComponentSelector);

      if (!sliderEl) {
        return;
      }

      this.applyDualSlides(sliderEl, signal);
    });

    document.addEventListener('shopify:section:load', this.handleSectionLoad, {
      signal,
    });
  };

  applyDualSlides = (sliderEl, signal) => {
    const swiper = sliderEl.slider;

    if (!swiper || signal.aborted) {
      return;
    }

    Object.assign(swiper.params, this.dualSliderParams);
    swiper.currentBreakpoint = false;
    swiper.setBreakpoint?.();
    swiper.update();
  };

  handleSectionLoad = (event) => {
    if (event.target.querySelector?.(this.dualSwiperSelector)) {
      this.init();
    }
  };

  destroy = () => {
    this.abortController?.abort();
    this.abortController = null;
  };
}

;// ./src/main.js

// JS goes here






window.addEventListener('load', () => {
  const classes = [
    new MenuUtilsNav(),
    new DeliveryDelay(),
    new ShopTheLook(),
    new HeaderScrollHide(),
    new Axeptio(),
  ];
  classes.forEach((classe) => classe.init());
});

/******/ })()
;